import React, { useEffect, useState, useMemo } from "react";
import { api, fmtDate } from "@/lib/api";
import { SectionTitle, EmptyState, Modal, Btn, TextArea } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, Clock, XCircle, Truck, TrendingUp, Building2, Package, CheckCircle2, AlertOctagon, FileDown } from "lucide-react";

const Card = ({ label, value, icon: Icon, accent = "#0A1F14", sub, testid }) => (
  <div className="brutal-card p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">{label}</div>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <div className="font-display font-black text-3xl leading-none" style={{ color: accent }}>{value}</div>
    {sub && <div className="font-mono-data text-[10px] text-[#777] mt-1">{sub}</div>}
  </div>
);

export default function ComprasDashboard() {
  const [data, setData] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // { req, action: 'approve' | 'reject' }
  const [obs, setObs] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pdfMonth, setPdfMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [reqScope, setReqScope] = useState("all"); // all | mine | units
  const { user } = useAuth();
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [dash, list] = await Promise.all([
        api.get("/compras/dashboard"),
        api.get("/requisitions", { headers: { "X-Unidade": "all" } }),
      ]);
      setData(dash.data);
      setReqs(list.data);
    } catch (e) { toast.error("Erro ao carregar dashboard"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = () => {
    // Abre em nova aba usando o token do axios via URL absoluta com Bearer via query? Não — usa axios blob.
    (async () => {
      try {
        const res = await api.get(`/compras/dashboard/pdf?month=${pdfMonth}`, { responseType: "blob" });
        const cd = res.headers["content-disposition"] || "";
        const match = cd.match(/filename="?([^"]+)"?/i);
        const fname = match ? match[1] : `compras_coomflona_${pdfMonth}.pdf`;
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Relatório PDF gerado");
      } catch (err) {
        toast.error("Erro ao gerar PDF");
      }
    })();
  };

  const askDecision = (req, action) => {
    setDecision({ req, action });
    setObs("");
  };

  const confirmDecision = async () => {
    if (!decision) return;
    const { req, action } = decision;
    if (action === "reject" && !obs.trim()) {
      toast.error("Informe o motivo da rejeição");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/requisitions/${req.id}/${action}`, { observacoes_aprovador: obs });
      toast.success(action === "approve" ? "Requisição aprovada" : "Requisição rejeitada");
      setDecision(null);
      setObs("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao processar");
    }
    setSubmitting(false);
  };

  const scopedReqs = useMemo(() => {
    if (!user) return reqs;
    if (reqScope === "mine") return reqs.filter(r => r.requester_id === user.id);
    if (reqScope === "units") return reqs.filter(r => r.requester_id !== user.id);
    return reqs;
  }, [reqs, reqScope, user]);
  const countMine = useMemo(() => user ? reqs.filter(r => r.status === "pendente" && r.requester_id === user.id).length : 0, [reqs, user]);
  const countUnits = useMemo(() => user ? reqs.filter(r => r.status === "pendente" && r.requester_id !== user.id).length : 0, [reqs, user]);

  if (loading) return <div className="p-8 font-mono-data text-sm">Carregando dashboard de Compras...</div>;
  if (!data) return <div className="p-8 font-mono-data text-sm">Sem dados</div>;

  const pendentes = scopedReqs.filter(r => r.status === "pendente").slice(0, 10);
  const totalPendentesScope = scopedReqs.filter(r => r.status === "pendente").length;
  const maxDaily = Math.max(1, ...(data.daily_series || []).map(d => d.count));

  return (
    <div data-testid="compras-dashboard">
      <SectionTitle
        action={
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={pdfMonth}
              onChange={(e) => setPdfMonth(e.target.value)}
              className="border-2 border-[#111] p-1.5 text-xs bg-white font-mono-data"
              data-testid="pdf-month-input"
              title="Mês do relatório"
            />
            <button
              onClick={downloadPdf}
              className="brutal-btn bg-[#0A1F14] text-white px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest inline-flex items-center gap-1.5"
              data-testid="download-pdf-btn"
            >
              <FileDown className="w-3.5 h-3.5" /> Baixar PDF
            </button>
          </div>
        }
      >
        Setor de Compras · Painel Global
      </SectionTitle>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card testid="kpi-pending" label="Pendentes agora" value={data.kpis.pending} icon={Clock} accent="#FF4F00" />
        <Card testid="kpi-approved" label="Aprovadas · 30d" value={data.kpis.approved_30d} icon={ClipboardCheck} accent="#1A7F37" />
        <Card testid="kpi-rejected" label="Rejeitadas · 30d" value={data.kpis.rejected_30d} icon={XCircle} accent="#E60000" />
        <Card testid="kpi-delivered" label="Entregues · 30d" value={data.kpis.delivered_30d} icon={Truck} accent="#3D6B57" />
        <Card testid="kpi-avg-hours" label="Média aprovação (h)" value={data.kpis.avg_approval_hours} icon={TrendingUp} accent="#0A1F14" sub="Tempo médio para decidir" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Curva diária */}
        <div className="brutal-card p-4 lg:col-span-2" data-testid="daily-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Curva de Requisições</div>
              <div className="font-display font-black text-lg">Últimos 30 dias</div>
            </div>
            <TrendingUp className="w-4 h-4 text-[#FF4F00]" />
          </div>
          <div className="h-40 flex items-end gap-1">
            {(data.daily_series || []).map((d) => {
              const h = Math.max(4, (d.count / maxDaily) * 140);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                  <div className="w-full bg-[#3D6B57] hover:bg-[#FF4F00] transition-colors" style={{ height: `${h}px` }} />
                </div>
              );
            })}
            {(!data.daily_series || data.daily_series.length === 0) && (
              <div className="w-full text-center py-8 font-mono-data text-xs text-[#777]">Sem dados no período</div>
            )}
          </div>
          <div className="font-mono-data text-[9px] uppercase tracking-widest text-[#999] mt-2">
            Hover nas barras para ver a data e a contagem
          </div>
        </div>

        {/* Por unidade */}
        <div className="brutal-card p-4" data-testid="by-unit-chart">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Por Unidade · 30d</div>
              <div className="font-display font-black text-lg">Distribuição</div>
            </div>
            <Building2 className="w-4 h-4 text-[#3D6B57]" />
          </div>
          {(data.by_unit || []).length === 0 && <div className="font-mono-data text-xs text-[#777] py-6 text-center">Sem dados</div>}
          {(data.by_unit || []).map((u) => {
            const total = data.by_unit.reduce((s, x) => s + x.count, 0) || 1;
            const pct = Math.round((u.count / total) * 100);
            return (
              <div key={u.unidade} className="mb-3 last:mb-0">
                <div className="flex items-center justify-between text-xs">
                  <div className="font-medium">{u.label}</div>
                  <div className="font-mono-data">{u.count} · {pct}%</div>
                </div>
                <div className="w-full bg-[#EEE] h-2 mt-1">
                  <div className="h-full bg-[#FF4F00]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top itens */}
      <div className="brutal-card p-4 mb-6" data-testid="top-items">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Top Itens Solicitados</div>
            <div className="font-display font-black text-lg">Últimos 30 dias</div>
          </div>
          <Package className="w-4 h-4 text-[#FF4F00]" />
        </div>
        {(data.top_items || []).length === 0 && <div className="font-mono-data text-xs text-[#777] py-4 text-center">Sem dados</div>}
        {(data.top_items || []).length > 0 && (
          <table className="brutal-table">
            <thead><tr><th>#</th><th>Código</th><th>Produto</th><th className="text-right">Qtd. Total</th><th className="text-right">Requisições</th></tr></thead>
            <tbody>
              {data.top_items.map((it, i) => (
                <tr key={i}>
                  <td className="font-mono-data font-bold">{i + 1}</td>
                  <td className="font-mono-data text-xs">{it.codigo}</td>
                  <td>{it.nome}</td>
                  <td className="text-right font-mono-data font-bold">{it.quantidade}</td>
                  <td className="text-right font-mono-data">{it.requests}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pendentes de aprovação */}
      <SectionTitle
        action={
          <div className="inline-flex border-2 border-[#111] bg-white" data-testid="scope-switch">
            {[
              { k: "all", label: `Todas (${countMine + countUnits})` },
              { k: "mine", label: `Só minhas (${countMine})` },
              { k: "units", label: `Das unidades (${countUnits})` },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setReqScope(opt.k)}
                data-testid={`scope-${opt.k}`}
                className={`px-3 py-1.5 text-[10px] font-display font-bold uppercase tracking-widest border-r-2 border-[#111] last:border-r-0 transition-colors ${
                  reqScope === opt.k ? "bg-[#FF4F00] text-white" : "bg-white text-[#111] hover:bg-[#FFF8E1]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        }
      >
        Aguardando sua decisão
      </SectionTitle>
      <div className="brutal-card overflow-x-auto" data-testid="pending-reqs">
        <table className="brutal-table">
          <thead><tr><th>Nº</th><th>Solicitante</th><th>Setor</th><th>Unidade</th><th>Itens</th><th>Data</th><th className="w-40">Ações</th></tr></thead>
          <tbody>
            {pendentes.length === 0 && <EmptyState message="Nenhuma requisição pendente 🎉" cols={7} />}
            {pendentes.map((r) => (
              <tr key={r.id} data-testid={`compras-pending-${r.id}`}>
                <td className="font-mono-data font-bold">{r.numero_sequencial || r.numero}</td>
                <td>{r.requester_nome}</td>
                <td className="text-sm text-[#555]">{r.sector_nome || "—"}</td>
                <td className="font-mono-data text-xs">{r.filial === "km117" ? "KM 117" : (r.filial === "cupari" ? "Cuparí" : r.filial)}</td>
                <td className="font-mono-data text-xs">{r.itens.length}</td>
                <td className="font-mono-data text-xs">{fmtDate(r.created_at)}</td>
                <td>
                  <div className="flex gap-2">
                    <a href={`/imprimir/requisicao/${r.id}`} target="_blank" rel="noreferrer" className="px-2 py-1 border-2 border-[#111] text-xs hover:bg-[#3D6B57] hover:text-white">Ver</a>
                    <button onClick={() => askDecision(r, "approve")} data-testid={`compras-approve-${r.id}`} className="px-2 py-1 border-2 border-[#1A7F37] bg-[#1A7F37] text-white text-xs font-bold hover:opacity-80">Aprovar</button>
                    <button onClick={() => askDecision(r, "reject")} data-testid={`compras-reject-${r.id}`} className="px-2 py-1 border-2 border-[#E60000] bg-[#E60000] text-white text-xs font-bold hover:opacity-80">Rejeitar</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPendentesScope > 10 && (
        <div className="mt-3 font-mono-data text-[10px] uppercase tracking-widest text-[#777] text-center">
          Exibindo 10 de {totalPendentesScope} pendentes ·
          <button onClick={() => nav("/requisicoes")} className="ml-2 text-[#FF4F00] underline">Ver todas</button>
        </div>
      )}

      {decision && (
        <Modal
          open={true}
          onClose={() => !submitting && setDecision(null)}
          title={decision.action === "approve" ? "Aprovar Requisição" : "Rejeitar Requisição"}
          size="md"
        >
          <div data-testid="decision-modal">
            <div className={`p-3 mb-4 border-2 ${decision.action === "approve" ? "border-[#1A7F37] bg-[#E6F4EA]" : "border-[#E60000] bg-[#FDECEA]"} flex items-start gap-3`}>
              {decision.action === "approve"
                ? <CheckCircle2 className="w-6 h-6 text-[#1A7F37] shrink-0" />
                : <AlertOctagon className="w-6 h-6 text-[#E60000] shrink-0" />}
              <div className="text-sm">
                <div className="font-bold">
                  Requisição <span className="font-mono-data">Nº {decision.req.numero_sequencial || decision.req.numero}</span>
                </div>
                <div className="text-[#555] mt-0.5">
                  Solicitante: <strong>{decision.req.requester_nome}</strong> · {decision.req.itens.length} item(ns)
                </div>
                <div className="text-[#555]">Unidade: {decision.req.filial === "km117" ? "KM 117 Anambé" : "Rio Cuparí"}</div>
              </div>
            </div>

            {decision.req.motivo && (
              <div className="mb-3">
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mb-1">Motivo do solicitante</div>
                <div className="border-2 border-[#EEE] p-2 text-sm bg-[#FAFAFA]">{decision.req.motivo}</div>
              </div>
            )}

            <TextArea
              label={decision.action === "approve" ? "Observação (opcional)" : "Motivo da rejeição *"}
              value={obs}
              onChange={setObs}
              testid="decision-obs"
              rows={3}
            />

            <div className="flex justify-end gap-2 mt-4">
              <Btn variant="secondary" onClick={() => setDecision(null)} disabled={submitting} data-testid="cancel-decision">Cancelar</Btn>
              <Btn
                variant={decision.action === "approve" ? "success" : "danger"}
                onClick={confirmDecision}
                disabled={submitting}
                data-testid="confirm-decision"
              >
                {submitting ? "Processando..." : (decision.action === "approve" ? "Aprovar Agora" : "Rejeitar Agora")}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
